import { createContext, useState, ReactNode, useCallback } from 'react'

import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { PostPlaidAuthDialog } from './AuthDialog'

interface PlaidError {
  message: string
  errorCode: string | number
}

export enum PlaidPostAuthDialogType {
  UPGRADE_FROM_MANUAL_STATEMENTS = 'UPGRADE_FROM_MANUAL_STATEMENTS',
  UPGRADE_FROM_LBA = 'UPGRADE_FROM_LBA',
}

export interface PostAuthDialog {
  type: PlaidPostAuthDialogType
  institutionId: string
  institutionName: string
}
interface PlaidLinkContextType {
  duplicateInstitution: string
  setDuplicateInstitution: (duplicateInstitution: string) => void
  plaidError: PlaidError | null
  setPlaidError: (plaidError: PlaidError | null) => void
  clearPlaidError: () => void
  postAuthDialog: PostAuthDialog | null
  setPostAuthDialog: (postAuthDialog: PostAuthDialog | null) => void
}

export const PlaidLinkContext = createContext<PlaidLinkContextType>({
  duplicateInstitution: '',
  setDuplicateInstitution: (_duplicateInstitution) => {},
  plaidError: null,
  setPlaidError: (_plaidError) => {},
  clearPlaidError: () => {},
  postAuthDialog: null,
  setPostAuthDialog: () => {},
})

export const PlaidLinkContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [duplicateInstitution, setDuplicateInstitution] = useState('')
  const [plaidError, setPlaidError] = useState<PlaidError | null>(null)
  const [postAuthDialog, setPostAuthDialog] = useState<PostAuthDialog | null>(
    null
  )
  const dispatch = useAppDispatch()

  const clearPlaidError = useCallback(async () => {
    await Promise.all([
      dispatch(fetchFinancialAccountsIfNeeded(true)),
      dispatch(fetchPlaidItemsIfNeeded(true)),
    ])
    setPlaidError(null)
  }, [dispatch])

  return (
    <PlaidLinkContext.Provider
      value={{
        duplicateInstitution,
        setDuplicateInstitution,
        plaidError,
        setPlaidError,
        clearPlaidError,
        postAuthDialog,
        setPostAuthDialog,
      }}
    >
      {children}
      <PostPlaidAuthDialog />
    </PlaidLinkContext.Provider>
  )
}
